import { translate } from '@/i18n-vue';
import { LocaleMessageValue } from 'vue-i18n';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import { useRTL } from '@/composables/useRTL';
import { Locales, Layout, RawPlace, MapMarker, PopupContent } from '@/models/sidebar/places.model';
import store from '@/store';

dayjs.extend(timezone);

const rtl = useRTL();

let locales: Locales;
let layout: Layout;

let staticPlacesCache: any[] = [];
let lastLocale = '';
let lastTheme: boolean | null = null;

const depopTimestampCache = new Map<string, number>();

// format number with comma like 1,000
function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDisplayValue(rawValue: string | number | undefined, numValue: number): string {
  if (!rawValue || String(rawValue).trim() === '' || String(rawValue) === '?') {
    return '?';
  }
  return formatNumber(numValue);
}

const formatDate = (d: string) => {
  if (!d) {
    return '?';
  }

  const date = dayjs.tz(d, 'Asia/Jerusalem');
  if (!date.isValid()) {
    return '?';
  }

  if (date.date() === 1) {
    return date.locale(store.state.locale).format('MMMM YYYY');
  }

  return date.locale(store.state.locale).format('D MMMM YYYY');
};

// Main processing function
function processPlaces(db: RawPlace[], currentDate?: string): MapMarker[] {
  const isDark = store.state.dark;
  const locale = store.state.locale;
  const currentTimestamp = currentDate ? new Date(currentDate).getTime() : 0;

  if (staticPlacesCache.length === 0 || lastLocale !== locale || lastTheme !== isDark) {
    lastLocale = locale;
    lastTheme = isDark;

    const chartBorder = isDark ? '#4a5568' : '#ddd';
    const chartEmpty = isDark ? '#2d3748' : '#f4f4f4';
    const crossOutColor = isDark ? '#a0aec0' : '#7f8c8d';
    const iconColor = isDark ? '#cbd5e0' : '#808080';
    const colorArab = isDark ? '#229954' : '#2ecc71';
    const colorJew = isDark ? '#1b85c2' : '#22a7f0';
    const colorPublic = isDark ? '#ab0847' : '#db0a5b';

    const tooltipHeader = translate('popupplace.tooltip.header');
    const comingSoonStr = `<i>${translate('popupplace.coming_soon')}</i>`;
    const notKnownStr = translate('popupplace.not_known');
    const notDepopulatedStr = translate('popupplace.not_depopulated');

    // eslint-disable-next-line complexity
    staticPlacesCache = db.map((place: RawPlace) => {
      const arabsPop = Number(place.arabs_pop) || 0;
      const jewsPop = Number(place.jews_pop) || 0;
      const totalPop = Number(place.total_pop) || 0;
      const arabsLand = Number(place.arabs_land) || 0;
      const jewsLand = Number(place.jews_land) || 0;
      const publicLand = Number(place.public_land) || 0;
      const totalLand = Number(place.total_land) || 0;

      const scaleFactor = 3.354;
      const rawRadius = totalPop > 0 ? Math.sqrt(totalPop) * scaleFactor : 0;
      const radius = Math.max(rawRadius, 50);

      let color = 'yellow';
      if (arabsPop > totalPop * 0.7) {
        color = 'green';
      }
      else if (jewsPop > totalPop * 0.7) {
        color = 'blue';
      }
      if (totalPop === 0) {
        color = 'grey';
      }

      // To be used when population data is unknown
      const safeNotes = Array.isArray(place.note) ? place.note.map(n => String(n)) : (place.note ? [String(place.note)] : []);
      if (safeNotes.includes('4002')) {
        color = 'green';
      }
      else if (safeNotes.includes('4001')) {
        color = 'blue';
      }

      let notesString = `<ul style='margin-bottom: 0; padding-left: 20px; text-align: start;'><li>${tooltipHeader}</li>`;

      const renderIdsToString = (data: string | string[], separator = '</br></br>'): string => {
        if (!data || data.length === 0) {
          if (data === place.reason_depopulated) {
            return notDepopulatedStr;
          }
          if (data === place.note) {
            return '';
          }
        }

        if (data.includes('/ or ?')) {
          return comingSoonStr;
        }
        if (data.includes('?')) {
          return notKnownStr;
        }
        if (data.includes('/')) {
          return notDepopulatedStr;
        }

        const formatId = (id: string) => {
          const translatedText = translate(`popupplace.tooltip.${id}`);
          return String(id) === '3200' ? `<i>${translatedText}</i>` : translatedText;
        };

        if (Array.isArray(data)) {
          return data.map(id => formatId(id)).join(separator);
        }

        return formatId(String(data));
      };

      // TODO: Add back notes starting in 100x
      if (place.note && place.note.length) {
        const notesArray = Array.isArray(place.note) ? place.note : [place.note];
        const visibleNotes = notesArray.filter(noteId => !String(noteId).startsWith('100'));
        if (visibleNotes.length > 0) {
          const bulletPoints = renderIdsToString(visibleNotes, '</li><li>');
          notesString += `<li>${bulletPoints}</li>`;
        }
      }

      notesString += '</ul>';

      const safeNotesString = notesString
        .replace(/\\/g, '\\\\')
        .replace(/'/g, '\\\'')
        .replace(/"/g, '&quot;')
        .replace(/\n/g, '<br>')
        .replace(/\r/g, '');

      const placeName = place[`place_${locale}` as keyof RawPlace];
      const districtName = place[`district_${locale}` as keyof RawPlace];

      if (currentTimestamp && place.date_depopulated) {
        const rawDate = place.date_depopulated.trim();

        if (rawDate !== '?' && rawDate !== '/' && rawDate !== '/ or ?') {
          let depopTime = depopTimestampCache.get(rawDate);

          if (depopTime === undefined) {
            const depopDate = new Date(rawDate);
            if (!isNaN(depopDate.getTime())) {
              if (/^\d{4}-\d{2}$/.test(rawDate)) {
                depopDate.setFullYear(depopDate.getFullYear(), depopDate.getMonth() + 1, 0);
              }
              depopTime = depopDate.getTime();
            }
            else {
              depopTime = 0;
            }
            depopTimestampCache.set(rawDate, depopTime);
          }
        }
      }

      const pop1 = totalPop ? Number(((arabsPop / totalPop) * 100).toFixed(2)) : 0;
      const pop2 = pop1 + (totalPop ? Number(((jewsPop / totalPop) * 100).toFixed(2)) : 0);

      const land1 = totalLand ? Number(((arabsLand / totalLand) * 100).toFixed(2)) : 0;
      const land2 = land1 + (totalLand ? Number(((jewsLand / totalLand) * 100).toFixed(2)) : 0);
      const land3 = land2 + (totalLand ? Number(((publicLand / totalLand) * 100).toFixed(2)) : 0);

      let depopTime = 0;
      if (place.date_depopulated) {
        const rawDate = place.date_depopulated.trim();
        if (rawDate !== '?' && rawDate !== '/' && rawDate !== '/ or ?') {
          const depopDate = new Date(rawDate);
          if (!isNaN(depopDate.getTime())) {
            if (/^\d{4}-\d{2}$/.test(rawDate)) {
              depopDate.setFullYear(depopDate.getFullYear(), depopDate.getMonth() + 1, 0);
            }
            depopTime = depopDate.getTime();
          }
        }
      }

      const chartPop = `<div style="width: 100px; height: 100px; border-radius: 50%; border: 1px solid ${chartBorder}; margin: 0 auto; background: conic-gradient(${colorArab} 0% ${pop1}%, ${colorJew} ${pop1}% ${pop2}%, ${chartEmpty} ${pop2}% 100%);"></div>`;
      const chartLand = `<div style="width: 100px; height: 100px; border-radius: 50%; border: 1px solid ${chartBorder}; margin: 0 auto; background: conic-gradient(${colorArab} 0% ${land1}%, ${colorJew} ${land1}% ${land2}%, ${colorPublic} ${land2}% ${land3}%, ${chartEmpty} ${land3}% 100%);"></div>`;

      const chart = `
      <div class="chart">
        <h5 style="text-align:center;">${locales['population']}</h5>
        ${chartPop}
      </div>
      <div class="chart">
        <h5 style="text-align:center;">${locales['land_ownership']}</h5>
        ${chartLand}
      </div>
      `;

      const popup: PopupContent = { ltr: '', rtl: '' };

      popup.ltr = `
      <div class="popupplace row" style="width: 100%; margin: 0; padding: 0; box-sizing: border-box;">
        <div class="col-xs-7" style="padding-left: 0; padding-right: 5px;">
          <b style="display: flex; align-items: center; margin-bottom: 4px; font-size:16px; line-height: 1.2;">
            {{NAME_PLACEHOLDER}}
            <span 
              class="info-icon glyphicon glyphicon-info-sign" 
              style="cursor: pointer; margin-left: 8px; color: ${iconColor}; top: 1px;"
              onclick="event.stopPropagation(); window.openPlaceInfo(event, '${safeNotesString}');"
            ></span>
          </b>
          <b>${locales['district']}</b>: ${districtName}<br/>
          
          <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['population']}</strong><br>
          <b>${locales['arab']}</b>: ${formatDisplayValue(place.arabs_pop, arabsPop)}<br>
          <b>${locales['jews']}</b>: ${formatDisplayValue(place.jews_pop, jewsPop)}<br>
          <b>${locales['total']}</b>: ${formatDisplayValue(place.total_pop, totalPop)}<br/>
          
          <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['land_ownership']}</strong><br>
          <b>${locales['arab']}</b>: ${formatDisplayValue(place.arabs_land, arabsLand)} ${locales['dunams']}<br>
          <b>${locales['jews']}</b>: ${formatDisplayValue(place.jews_land, jewsLand)} ${locales['dunams']}<br>
          <b>${locales['public']}</b>: ${formatDisplayValue(place.public_land, publicLand)} ${locales['dunams']}<br>
          <b>${locales['total']}</b>: ${formatDisplayValue(place.total_land, totalLand)} ${locales['dunams']}<br/>
          
          <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['occupation']}</strong><br>
          <b>${locales['date_occupied']}</b>: ${comingSoonStr}<br>
          <b>${locales['date_depopulated']}</b>: ${place.date_depopulated === '/ or ?' ? comingSoonStr : place.date_depopulated === '?' ? notKnownStr : place.date_depopulated === '/' ? notDepopulatedStr : (formatDate(place.date_depopulated) === '?' ? notKnownStr : formatDate(place.date_depopulated))}<br>
          <b>${locales['reason_depopulated']}</b>: ${renderIdsToString(place.reason_depopulated, ' / ')}<br>
        </div>

        <div class="col-xs-5 mt-1" style="padding: 0; display: flex; flex-direction: column; align-items: center;">
          ${totalPop > 0 ? chart : ''}
        </div>
      </div>
      `;

      popup.rtl = `
        <div class="popupplace row" dir="rtl" style="width: 100%; margin: 0; padding: 0; box-sizing: border-box; text-align: right;">
          <div class="col-xs-7"style="padding-right: 0; padding-left: 5px;">
            <b style="display: flex; align-items: center; margin-bottom: 4px; font-size:16px; line-height: 1.2;">
              {{NAME_PLACEHOLDER}}
              <span 
                class="info-icon glyphicon glyphicon-info-sign" 
                style="cursor: pointer; margin-right: 8px; color: ${iconColor}; top: 1px;"
                onclick="event.stopPropagation(); window.openPlaceInfo(event, '${safeNotesString}');"
              ></span>
            </b>
            <b>${locales['district']}</b>: ${districtName}<br/>
            
            <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['population']}</strong><br>
            <b>${locales['arab']}</b>: ${formatDisplayValue(place.arabs_pop, arabsPop)}<br>
            <b>${locales['jews']}</b>: ${formatDisplayValue(place.jews_pop, jewsPop)}<br>
            <b>${locales['total']}</b>: ${formatDisplayValue(place.total_pop, totalPop)}<br/>
            
            <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['land_ownership']}</strong><br>
            <b>${locales['arab']}</b>: ${formatDisplayValue(place.arabs_land, arabsLand)} ${locales['dunams']}<br>
            <b>${locales['jews']}</b>: ${formatDisplayValue(place.jews_land, jewsLand)} ${locales['dunams']}<br>
            <b>${locales['public']}</b>: ${formatDisplayValue(place.public_land, publicLand)} ${locales['dunams']}<br>
            <b>${locales['total']}</b>: ${formatDisplayValue(place.total_land, totalLand)} ${locales['dunams']}<br/>
            
            <strong style="font-size:13px; display:inline-block; margin-top: 8px;">${locales['occupation']}</strong><br>
            <b>${locales['date_occupied']}</b>: ${comingSoonStr}<br>
            <b>${locales['date_depopulated']}</b>: ${place.date_depopulated === '/ or ?' ? comingSoonStr : place.date_depopulated === '?' ? notKnownStr : place.date_depopulated === '/' ? notDepopulatedStr : (formatDate(place.date_depopulated) === '?' ? notKnownStr : formatDate(place.date_depopulated))}<br>
            <b>${locales['reason_depopulated']}</b>: ${renderIdsToString(place.reason_depopulated, ' / ')}<br>
        </div>

        <div class="col-xs-5 mt-1" style="padding: 0; display: flex; flex-direction: column; align-items: center;">
          ${totalPop > 0 ? chart : ''}
        </div>
      </div>
      `;

      const rawPopupHtml = popup[layout];

      const nameNormal = String(placeName || '');
      const nameCrossed = `<span style="text-decoration: line-through; color: ${crossOutColor};">${placeName}</span>`;

      return {
        id: place._id,
        tooltip: placeName,
        location: place.location,
        district: districtName,
        radius: radius,
        color: color,
        depopTime: depopTime,
        popupNormal: rawPopupHtml.replace('{{NAME_PLACEHOLDER}}', nameNormal),
        popupCrossed: rawPopupHtml.replace('{{NAME_PLACEHOLDER}}', nameCrossed)
      };
    });
  }

  return staticPlacesCache.map(place => {
    const isCrossedOut = place.depopTime !== 0 && currentTimestamp >= place.depopTime;

    return {
      id: place.id,
      tooltip: place.tooltip,
      location: place.location,
      district: place.district,
      radius: place.radius,
      color: place.color,
      isCrossedOut: isCrossedOut,
      popup: isCrossedOut ? place.popupCrossed : place.popupNormal
    };
  });
}

function cachePlaces(locale: Locales | LocaleMessageValue, _layout: Layout): void {
  if (typeof locale === 'object' && locale !== null && !Array.isArray(locale)) {
    if ('population' in locale && 'district' in locale) {
      locales = locale as Locales;
    }
    else {
      const messageDict = locale;
      locales = {
        population: String(messageDict.population || 'Population'),
        land_ownership: String(messageDict.land_ownership || 'Land Ownership'),
        district: String(messageDict.district || 'District'),
        arab: String(messageDict.arab || 'Arab'),
        jews: String(messageDict.jews || 'Jews'),
        total: String(messageDict.total || 'Total'),
        dunams: String(messageDict.dunams || 'Dunams'),
        public: String(messageDict.public || 'Public'),
        occupation: String(messageDict.occupation || 'Occupation'),
        date_occupied: String(messageDict.date_occupied || 'Date Occupied'),
        date_depopulated: String(messageDict.date_depopulated || 'Date Depopulated'),
        reason_depopulated: String(messageDict.reason_depopulated || 'Reason Depopulated'),
      };
    }
  }
  else {
    locales = {
      population: 'Population',
      land_ownership: 'Land Ownership',
      district: 'District',
      arab: 'Arab',
      jews: 'Jews',
      total: 'Total',
      dunams: 'Dunams',
      public: 'Public',
      occupation: 'Occupation',
      date_occupied: 'Date Occupied',
      date_depopulated: 'Date Depopulated',
      reason_depopulated: 'Reason Depopulated',
    };
  }
  layout = _layout;
}

export { processPlaces, cachePlaces };
